import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { HStack, IconButton, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Text, Tooltip } from '@bonusly/design-system';

import type { DoodleCardDraft } from '../../../../lib/bizy/types/ui-blocks';
import { useBizyUiBlockActionDispatcher } from '../context/actions-context';
import { DoodleOutline, GifOutline, MagicWandOutline, SparklesOutline } from '../lib/icons';

/** Small section label inside the menu (mirrors a MenuGroup title, with an optional leading icon). */
function MenuSectionLabel({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
	return (
		<HStack as="span" spacing={1} align="center" px={2} py={1} color="text.softest">
			{icon}
			<Text as="span" fontSize="sm">
				{children}
			</Text>
		</HStack>
	);
}

/**
 * The "Make it special" wand menu on the recognition draft card (it replaces
 * the copy button). Offers the on-demand enrichments that apply to a draft:
 *   - "Add a GIF"  -> `show_gif_picker` (backend generates contextual pills at
 *     click time and renders the picker on demand).
 *   - "Draw a doodle" -> `draft_doodle` (unchanged doodle flow).
 * Both carry the `draft` the user currently sees (the focused variant) so the
 * backend's pill generation / re-render matches the visible tab. Disabled while
 * a gif attach/remove is in flight so a new turn can't race it.
 */
export function MakeItSpecialMenu({ draft, isDisabled }: { draft: DoodleCardDraft; isDisabled?: boolean }) {
	const { t } = useTranslation('admin_bizy');
	const dispatch = useBizyUiBlockActionDispatcher();

	const handleAddGif = () => {
		if (isDisabled) return;
		dispatch({ type: 'show_gif_picker', payload: draft });
	};
	const handleDrawDoodle = () => {
		if (isDisabled) return;
		dispatch({ type: 'draft_doodle', payload: draft });
	};

	return (
		<Menu placement="top-start" isLazy>
			<Tooltip label={t('uiBlocks.makeItSpecial.tooltip')} placement="top">
				<MenuButton
					as={IconButton}
					aria-label={t('uiBlocks.makeItSpecial.tooltip')}
					icon={<MagicWandOutline boxSize={5} />}
					size="sm"
					variant="ghost"
					borderRadius="md"
					borderColor="border.default"
					borderWidth="1px"
					isDisabled={isDisabled}
					_hover={{ bg: 'grayAlpha.100' }}
				/>
			</Tooltip>
			<MenuList minW="220px" py={1}>
				<MenuSectionLabel>{t('uiBlocks.makeItSpecial.sectionSpecial')}</MenuSectionLabel>
				<MenuItem icon={<GifOutline boxSize={6} />} onClick={handleAddGif} fontSize="sm" height="40px" color="text.default">
					{t('uiBlocks.makeItSpecial.addGif')}
				</MenuItem>
				<MenuDivider />
				<MenuSectionLabel icon={<SparklesOutline boxSize={4} color="text.softest" />}>{t('uiBlocks.makeItSpecial.sectionBizy')}</MenuSectionLabel>
				<MenuItem icon={<DoodleOutline boxSize={6} />} onClick={handleDrawDoodle} fontSize="sm" height="40px" color="text.default">
					{t('uiBlocks.makeItSpecial.drawDoodle')}
				</MenuItem>
			</MenuList>
		</Menu>
	);
}
